import { IComboBoxOption } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPCheckbox,
  IPComboBox,
  IPCommandBarButton,
  IPIconButton,
  IPLink,
  IPScrollablePane,
  IPSeparator,
  IPSpinner,
  IPText,
} from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import Accordion from 'components/Accordion/Accordion';
import { IQuickFilterItemRenderer } from 'components/Filter/QuickFilter';
import { GlobalFilterGroupKeys } from 'components/GlobalFilter/GlobalFilterGroupKeys';
import {
  clearFilterKeepGlobalFilter,
  countGlobalFilters,
  globalFilterCount,
} from 'components/GlobalFilter/GlobalFilterHelper';
import EntityPicker from 'components/Pickers/EntityPicker';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import SingleTagPicker from 'components/Pickers/SingleTagPicker';
import UserPicker from 'components/Pickers/UserPicker';
import { KeyValueTag } from 'components/Tags/KeyValueTag';
import { ISingleTag, SingleTag } from 'components/Tags/SingleTag';
import { getEntity } from 'globalFunctions';
import {
  cancelIcon,
  filterICon,
  globalComboContainerHeight,
  globalSceneBarItemStyles,
  globalStackStylesHeight100PaddingSmall,
  globalStackTokensGapSmall,
} from 'globalStyles';
import Asset from 'models/asset/asset';
import { isAuditCriteriaPreviewEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import { AuthSchema } from 'models/auth/authSchema';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import Control from 'models/control';
import Entity, { EntityTypes } from 'models/entity';
import Norm from 'models/norm';
import Objective from 'models/objective/objective';
import Process from 'models/process/process';
import Risk from 'models/risk';
import { LegacyOutlookSeriesEnabled } from 'models/setting';
import Tag, { TagSystemTypes } from 'models/tag';
import Task, { TaskWorkableTypes } from 'models/tasks/task';
import { TaskKinds } from 'models/tasks/taskType';
import { TaskState } from 'models/tasks/taskHelperClasses';
import Theme from 'models/theme';
import User from 'models/user';
import { TaskTagFilterGroupAnd } from 'models/userSetting';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAsset, apiGetAssets } from 'services/Api/assetService';
import { apiGetControl, apiGetControls } from 'services/Api/controlService';
import { apiGetObjective, apiGetObjectives } from 'services/Api/objectiveService';
import { apiGetProcess, apiGetProcesses } from 'services/Api/processService';
import { apiGetRisk, apiGetRisks } from 'services/Api/riskService';
import { apiGetTheme, apiGetThemes } from 'services/Api/themeService';
import { apiRequest } from 'services/Auth/authConfig';
import {
  convertAuthSchemaString,
  FeatureTypes,
  hasUserDataPermission,
  hasUserFeature,
} from 'services/Auth/featurePermissions';
import AppError from 'utils/appError';
import { FilterTaskGroupKeys } from './FilterTaskGroupKeys';
import { controlIdSearchPrepend, themeIdSearchPrepend } from './FilterTasksHelper';

interface IFilterTasks {
  toggleDialog: (state: boolean) => void;
  isOpen: boolean;
  updateSelectedFilter: Function;
  selectedFilter: string[];
  lockedFilterValues?: string[];
  hideClear?: boolean;
  showWorkableTypeFilter: boolean;
  showTemplateFilter: boolean;
}

interface ICheckbox {
  key: string;
  name: string;
  value: string;
  label: string;
}

//
// The context quick filter shows the name of the entity that the tasks belong to.
// The filter value holds the type and the id of that entity.
//
const getContextEntityFromFilterValue = (filterValue: string): { entityId: number; entityType: EntityTypes } => {
  if (filterValue.startsWith(controlIdSearchPrepend)) {
    return {
      entityId: Number.parseInt(filterValue.substring(controlIdSearchPrepend.length)),
      entityType: EntityTypes.Control,
    };
  }

  if (filterValue.startsWith(themeIdSearchPrepend)) {
    return {
      entityId: Number.parseInt(filterValue.substring(themeIdSearchPrepend.length)),
      entityType: EntityTypes.Requirement,
    };
  }

  return {
    entityId: Number.parseInt(filterValue.split('_')[1]),
    entityType: Number.parseInt(filterValue.split('_')[0]),
  };
};

//
// Render the filter as a quick filter
// Each quick filter must be implemented here
// 1. When a quick filter cannot be rendered, return null so the filter is removed
// 2. When a quick filter needs to be loaded (e.g. control name), return a small <IPSpinner> while loading
//
export const QuickFilterTasksRenderer = (props: IQuickFilterItemRenderer): JSX.Element | null => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'tasks', 'adminAuth', 'tag']);
  const [entity, setEntity] = useState<Entity | undefined>(undefined);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  const filterGroup = props.filter.split('$');
  const filterType = filterGroup[0] + '$';
  const filterValue = filterGroup[1];
  const contextEntity =
    filterType === FilterTaskGroupKeys.context ? getContextEntityFromFilterValue(filterValue) : undefined;
  const renderTextOrNull = (label: string): JSX.Element | null => (label ? <IPText>{label}</IPText> : null);

  useEffect(() => {
    let isActive = true;

    const loadEntity = async (entityId: number, type: EntityTypes) => {
      try {
        //load Data
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        let entity: Entity | undefined = undefined;

        switch (type) {
          case EntityTypes.Control:
            const control = await apiGetControl(entityId, false, false, accessToken, appContext.globalDataCache);
            if (control) {
              entity = new Entity();
              entity.entityId = control.controlId;
              entity.entityName = control.name;
              entity.entityCode = control.code;
              entity.typeOfEntity = type;
            }
            break;
          case EntityTypes.Requirement:
            const theme = await apiGetTheme(entityId, false, false, accessToken, appContext.globalDataCache);
            if (theme) {
              entity = new Entity();
              entity.entityId = theme.themeId;
              entity.entityName = theme.name;
              entity.entityCode = theme.code;
              entity.typeOfEntity = type;
            }
            break;
          case EntityTypes.Objective:
            const objective = await apiGetObjective(entityId, accessToken, appContext.globalDataCache);
            if (objective) {
              entity = new Entity();
              entity.entityId = objective.objectiveId;
              entity.entityName = objective.name;
              entity.entityCode = objective.code;
              entity.typeOfEntity = type;
            }
            break;
          case EntityTypes.Process:
            const process = await apiGetProcess(entityId, accessToken, appContext.globalDataCache);
            if (process) {
              entity = new Entity();
              entity.entityId = process.processId;
              entity.entityName = process.name;
              entity.entityCode = process.code;
              entity.typeOfEntity = type;
            }
            break;
          case EntityTypes.Asset:
            const asset = await apiGetAsset(entityId, accessToken, appContext.globalDataCache);
            if (asset) {
              entity = new Entity();
              entity.entityId = asset.assetId;
              entity.entityName = asset.name;
              entity.entityCode = asset.code;
              entity.typeOfEntity = type;
            }
            break;
          case EntityTypes.Risk:
            const risk = await apiGetRisk(entityId, accessToken, appContext);
            if (risk && risk.risks.length > 0) {
              entity = new Entity();
              entity.entityId = risk.risks[0].riskId;
              entity.entityName = risk.risks[0].name;
              entity.entityCode = risk.risks[0].code;
              entity.typeOfEntity = type;
            }
            break;
          default:
            throw new AppError('not implemented');
        }

        if (isActive) setEntity(entity);
      } catch (err) {
        appContext.setError(err);
      } finally {
        if (isActive) setHasLoaded(true);
      }
    };

    if (contextEntity?.entityId) {
      loadEntity(contextEntity.entityId, contextEntity.entityType);
    }

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextEntity?.entityId, contextEntity?.entityType]);

  //
  // Main render
  //

  switch (filterType) {
    case FilterTaskGroupKeys.assignment:
      if (filterValue === 'notassigned') {
        return <IPText>{t('tasks:Filter.NotAssignment')}</IPText>;
      } else {
        if (!appContext.globalDataCache.users.has(filterValue)) return null;
        const user = appContext.globalDataCache.users.get(filterValue);

        return <IPPersona imageUrl={user.getPhotoURLObj()} size={IPPersonaSize.size24} text={user.name} />;
      }
    case FilterTaskGroupKeys.status:
      if (filterValue === 'open') {
        return <IPText>{t('tasks:Filter.StatusOpen')}</IPText>;
      } else {
        return <IPText>{appContext.globalDataCache.taskStates.get(Number(filterValue)).state}</IPText>;
      }
    case FilterTaskGroupKeys.shared:
      if (filterValue === 'everyone') {
        return <IPPersona size={IPPersonaSize.size24} text={t('adminAuth:AuthSchemaPicker.Placeholder')} />;
      } else {
        if (!appContext.globalDataCache.authSchemas.has(Number(filterValue))) return null;

        return (
          <IPPersona
            size={IPPersonaSize.size24}
            text={appContext.globalDataCache.authSchemas.get(Number(filterValue)).name}
          />
        );
      }
    case FilterTaskGroupKeys.startDate:
      let labelStartDate: string = '';
      switch (filterValue) {
        case 'startToday':
          labelStartDate = t('tasks:Filter.TodayOrPassed');
          break;
        case 'startNextWeek':
          labelStartDate = t('tasks:Filter.NextWeek');
          break;
        case 'startNextMonth':
          labelStartDate = t('tasks:Filter.NextMonth');
          break;
        case 'startNextQuarter':
          labelStartDate = t('tasks:Filter.NextQuarter');
          break;
      }

      return labelStartDate ? <IPText>{`${t('tasks:QuickFilter.StartDate')}: ${labelStartDate}`}</IPText> : null;
    case FilterTaskGroupKeys.deadline:
      let labelDeadline: string = '';
      switch (filterValue) {
        case 'deadlineTooLate':
          labelDeadline = t('tasks:Filter.TooLate');
          break;
        case 'deadlineToday':
          labelDeadline = t('tasks:Filter.Today');
          break;
        case 'deadlineThisWeek':
          labelDeadline = t('tasks:Filter.ThisWeek');
          break;
        case 'deadlineNextWeek':
          labelDeadline = t('tasks:Filter.NextWeek');
          break;
      }

      return labelDeadline ? <IPText>{`${t('tasks:QuickFilter.Deadline')}: ${labelDeadline}`}</IPText> : null;
    case FilterTaskGroupKeys.followUp:
      let labelFollowUp: string = '';
      switch (filterValue) {
        case 'true':
          labelFollowUp = t('tasks:Filter.FollowUp');
          break;
        case 'false':
          labelFollowUp = t('tasks:Filter.NotFollowUp');
          break;
      }

      return renderTextOrNull(labelFollowUp);
    case FilterTaskGroupKeys.tag:
      const tagId = Number(filterValue);
      if (!appContext.globalDataCache.tags.has(tagId)) return null;
      const tag = appContext.globalDataCache.tags.get(tagId);

      return <KeyValueTag tag={tag} maxWidth={300} />;
    case FilterTaskGroupKeys.tagGroup:
      const tagGroupName = filterValue;
      const tagGroup = appContext.globalDataCache.tags.getFirstForGroup(tagGroupName);
      if (!tagGroup) return null;

      return <SingleTag tag={tagGroup} maxWidth={300} />;
    case FilterTaskGroupKeys.standard:
      if (appContext.globalFilter.some((f) => f.startsWith(GlobalFilterGroupKeys.standard))) {
        return null;
      }

      const normId = Number.parseInt(filterValue);
      if (!appContext.globalDataCache.norms.has(normId)) return null;
      const norm = appContext.globalDataCache.norms.get(normId);

      return <IPText>{norm.name}</IPText>;
    case FilterTaskGroupKeys.context:
      if (contextEntity?.entityId) {
        if (entity) {
          return <IPText>{entity.entityName}</IPText>;
        } else if (!hasLoaded) {
          return <IPSpinner />;
        }
      }

      return null; //this will remove the filter
    case FilterTaskGroupKeys.workableTaskType:
      const workableType = Number.parseInt(filterValue) as TaskWorkableTypes;
      const typeLabel = Task.getWorkableTypeText(workableType, t);

      return renderTextOrNull(typeLabel);
    case FilterTaskGroupKeys.taskKind:
      return renderTextOrNull(
        Number.parseInt(filterValue) === (TaskKinds.Assessment as number) ? t('tasks:Filter.Assessments') : '',
      );
    case FilterTaskGroupKeys.template:
      const templateId = Number.parseInt(filterValue);
      const template = appContext.globalDataCache.templates.get(templateId);
      if (template) {
        return <IPText>{template.name}</IPText>;
      }

      return null;
    case FilterTaskGroupKeys.tagCategory:
      const id = Number.parseInt(filterValue);
      if (id) {
        // An unknown system-type id resolves to an empty label. Return null so
        // the dead chip is dropped instead of rendered as a blank checkbox.
        const categoryLabel = Tag.GetSystemTypeText(id, t);

        return renderTextOrNull(categoryLabel);
      }

      return null;
    default:
      return null;
  }
};

//
// Filter component
//
const FilterTasks = (props: IFilterTasks) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'tasks', 'adminAuth']);
  const [allEntities, setAllEntities] = useState<Entity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [taskFilterAnd, setTaskFilterAnd] = useState<boolean>(
    appContext.globalDataCache.userSettings.get(TaskTagFilterGroupAnd) as boolean,
  );
  const [legacyOutlookSeriesEnabled] = useState<boolean>(
    appContext.globalDataCache.settings.get(LegacyOutlookSeriesEnabled) as boolean,
  );

  //
  // Effects
  //
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //
  // Loading
  //
  const loadData = async () => {
    try {
      setIsLoading(true);
      await appContext.globalDataCache.templates.getItems();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Constants
  //
  const followUpFilterList: ICheckbox[] = [
    {
      key: 'true',
      name: 'followUp',
      value: 'true',
      label: t('tasks:Filter.FollowUp'),
    },
    { key: 'false', name: 'notfollowUp', value: 'false', label: t('tasks:Filter.NotFollowUp') },
  ];

  const startDateList: ICheckbox[] = [
    { key: 'startToday', name: 'startToday', value: 'startToday', label: t('tasks:Filter.TodayOrPassed') },
    {
      key: 'startNextWeek',
      name: 'startNextWeek',
      value: 'startNextWeek',
      label: t('tasks:Filter.NextWeek'),
    },
    {
      key: 'startNextMonth',
      name: 'startNextMonth',
      value: 'startNextMonth',
      label: t('tasks:Filter.NextMonth'),
    },
    {
      key: 'startNextQuarter',
      name: 'startNextQuarter',
      value: 'startNextQuarter',
      label: t('tasks:Filter.NextQuarter'),
    },
  ];

  const deadlineList: ICheckbox[] = [
    {
      key: 'deadlineTooLate',
      name: 'deadlineTooLate',
      value: 'deadlineTooLate',
      label: t('tasks:Filter.TooLate'),
    },
    { key: 'deadlineToday', name: 'deadlineToday', value: 'deadlineToday', label: t('tasks:Filter.Today') },
    {
      key: 'deadlineThisWeek',
      name: 'deadlineThisWeek',
      value: 'deadlineThisWeek',
      label: t('tasks:Filter.ThisWeek'),
    },
    {
      key: 'deadlineNextWeek',
      name: 'deadlineNextWeek',
      value: 'deadlineNextWeek',
      label: t('tasks:Filter.NextWeek'),
    },
  ];

  //
  // Remove functions
  //
  const removeFilterContext = () => {
    let items: string[] = [...props.selectedFilter];
    items = items.filter((f) => !f.startsWith(FilterTaskGroupKeys.context));
    props.updateSelectedFilter(items);
  };

  const updateFilterContext = (key: string) => {
    let items: string[] = [...props.selectedFilter];
    items = items.filter(
      (f) =>
        !f.startsWith(FilterTaskGroupKeys.context + controlIdSearchPrepend) &&
        !f.startsWith(FilterTaskGroupKeys.context + themeIdSearchPrepend),
    );
    items.push(key);
    props.updateSelectedFilter(items);
  };

  //
  // Functions to get specific values from the filter
  //
  const getUserIdFromFilter = (filter: string[]): string | undefined => {
    const assignment = filter.find(
      (f) => f.startsWith(FilterTaskGroupKeys.assignment) && f !== FilterTaskGroupKeys.assignment + 'notassigned',
    );

    return assignment?.split('$')[1];
  };

  const getTagsFromFilter = (filter: string[]): Tag[] => {
    const tags: Tag[] = [];
    filter.forEach((f) => {
      if (f.startsWith(FilterTaskGroupKeys.tag)) {
        const tagId = f.substring(FilterTaskGroupKeys.tag.length);
        const id = Number.parseInt(tagId);
        if (appContext.globalDataCache.tags.has(id)) {
          const tag = appContext.globalDataCache.tags.get(id);
          tags.push(tag);
        }
      }
    });

    return tags;
  };

  const getTagGroupsFromFilter = (filter: string[]): ISingleTag[] => {
    const tagGroups: ISingleTag[] = [];
    filter.forEach((f) => {
      if (f.startsWith(FilterTaskGroupKeys.tagGroup)) {
        const tagGroup = f.substring(FilterTaskGroupKeys.tagGroup.length);
        const firstTagWithGroup = appContext.globalDataCache.tags.getFirstForGroup(tagGroup);
        if (firstTagWithGroup) {
          const newTagGroup: ISingleTag = {
            tagId: firstTagWithGroup.tagId,
            tagName: tagGroup,
            tagColor: firstTagWithGroup.tagColor,
          };
          tagGroups.push(newTagGroup);
        }
      }
    });

    return tagGroups;
  };

  const getContextEntityFromFilter = (): Entity | undefined => {
    let entity: Entity | undefined = undefined;
    const filter = props.selectedFilter.find((f) => f.startsWith(FilterTaskGroupKeys.context));

    if (filter) {
      //control
      if (filter.startsWith(FilterTaskGroupKeys.context + controlIdSearchPrepend)) {
        entity = new Entity();
        entity.typeOfEntity = EntityTypes.Control;
        entity.entityId = Number.parseInt(
          filter.substring(FilterTaskGroupKeys.context.length + controlIdSearchPrepend.length),
        );
      } //requirement
      else if (filter.startsWith(FilterTaskGroupKeys.context + themeIdSearchPrepend)) {
        entity = new Entity();
        entity.typeOfEntity = EntityTypes.Requirement;
        entity.entityId = Number.parseInt(
          filter.substring(FilterTaskGroupKeys.context.length + themeIdSearchPrepend.length),
        );
      }
      //other
      else {
        const value = filter?.split('$')[1];
        const typeid = value?.split('_') ?? [];
        const type = Number(typeid[0]);
        const id = Number(typeid[1]);
        if (type && id && !isNaN(type) && !isNaN(id)) {
          entity = new Entity();
          entity.entityId = id;
          entity.typeOfEntity = type;
        }
      }
    }

    return entity;
  };

  const getTemplateEntityFromFilter = (): number | undefined => {
    const filter = props.selectedFilter.find((f) => f.startsWith(FilterTaskGroupKeys.template));

    const value = filter?.split('$')[1];
    const id = Number(value);
    if (id && !isNaN(id)) {
      return id;
    }

    return undefined;
  };

  //
  // Functions to get count of specific filters
  //
  const getSelectedFilterCount = (type: string): number => {
    if (props.selectedFilter.length > 0) {
      return props.selectedFilter.filter((f) => f.startsWith(type))?.length;
    }

    return 0;
  };

  const getTotalFilterCount = (filter: string[]): number => {
    const count = filter.filter((f) => !f.startsWith(FilterTaskGroupKeys.useAndForTagGroups)).length;

    return count - countGlobalFilters(appContext, filter, FilterTaskGroupKeys);
  };

  //
  // Functions to load data for specific filters
  //
  const loadDataForPicker = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      const _allThemesPromise = apiGetThemes(accessToken, appContext.globalDataCache);
      const _allProcessesPromise = apiGetProcesses(accessToken, appContext.globalDataCache);
      const _allObjectivesPromise = apiGetObjectives(accessToken, appContext.globalDataCache);
      const _allControlsPromise = apiGetControls(accessToken, appContext.globalDataCache);
      const _allAssetsPromise = apiGetAssets(accessToken, appContext.globalDataCache);
      const _allRisksPromise = apiGetRisks(accessToken, appContext);

      const [_allThemesData, _allProcessesData, _allObjectivesData, _allControlsData, _allAssetsData, _allRisksData] =
        await Promise.allSettled([
          _allThemesPromise,
          _allProcessesPromise,
          _allObjectivesPromise,
          _allControlsPromise,
          _allAssetsPromise,
          _allRisksPromise,
        ]);

      let _allEntities: Entity[] = [];
      let errorMessage = '';

      if (_allThemesData.status === 'fulfilled') {
        _allThemesData.value.forEach((_theme: Theme) => {
          _allEntities.push(getEntity(_theme, _theme.getAutSchemaIds(appContext).join(',')));
        });
      } else {
        errorMessage += `${t('theme:Title')} : ${_allThemesData.reason}`;
      }

      if (_allObjectivesData.status === 'fulfilled') {
        _allObjectivesData.value.forEach((_objective: Objective) => {
          _allEntities.push(getEntity(_objective, _objective.authSchemaId?.toString()));
        });
      } else {
        errorMessage += `${t('objective:Title')} : ${_allObjectivesData.reason}`;
      }

      if (_allProcessesData.status === 'fulfilled') {
        _allProcessesData.value.forEach((_process: Process) => {
          _allEntities.push(getEntity(_process, _process.authSchemaId?.toString()));
        });
      } else {
        errorMessage += `${t('process:Title')} : ${_allProcessesData.reason}`;
      }

      if (_allControlsData.status === 'fulfilled') {
        _allControlsData.value.forEach((_control: Control) => {
          _allEntities.push(getEntity(_control, _control.getAutSchemaIds(appContext).join(',')));
        });
      } else {
        errorMessage += `${t('control:Title')} : ${_allControlsData.reason}`;
      }

      if (_allAssetsData.status === 'fulfilled') {
        _allAssetsData.value.forEach((_asset: Asset) => {
          _allEntities.push(getEntity(_asset, _asset.authSchemaId?.toString()));
        });
      } else {
        errorMessage += `${t('asset:Title')} : ${_allAssetsData.reason}`;
      }

      if (_allRisksData.status === 'fulfilled') {
        _allRisksData.value.risks.forEach((_risk: Risk) => {
          _allEntities.push(getEntity(_risk, _risk.authSchemaId?.toString()));
        });
      } else {
        errorMessage += `${t('process:Title')} : ${_allRisksData.reason}`;
      }

      if (errorMessage) appContext.setError(errorMessage);

      //filter out all items where the current user does not have update permissions
      _allEntities = _allEntities.filter((l) => {
        return hasUserDataPermission(appContext, convertAuthSchemaString(l.data), AuthSchemaLineOperation.Update);
      });

      setAllEntities(_allEntities);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Generic helpers
  //

  const handleFilterClear = () => {
    clearFilter();
    props.toggleDialog(false);
  };

  const clearFilter = () => {
    const newFilter = clearFilterKeepGlobalFilter(appContext, props.selectedFilter, FilterTaskGroupKeys);
    // Keep a Standard View's locked defining filter even when clearing all filters.
    const withLocked = [...newFilter, ...(props.lockedFilterValues ?? []).filter((v) => !newFilter.includes(v))];
    props.updateSelectedFilter(withLocked);
  };

  const clearFilterForType = (group: FilterTaskGroupKeys) => {
    let newFilter = clearFilterKeepGlobalFilter(appContext, props.selectedFilter, FilterTaskGroupKeys);
    // Keep locked values even if they belong to the group being cleared.
    newFilter = newFilter.filter((f) => (props.lockedFilterValues?.includes(f) ? true : !f.startsWith(group)));
    props.updateSelectedFilter(newFilter);
  };

  const handleSelectChange = (key: string | string[], removeAllFirst?: boolean) => {
    let items: string[] = [...props.selectedFilter];

    if (removeAllFirst) {
      if (key instanceof Array) {
        key.forEach((k) => {
          const filterGroup = k.split('$');
          const filterType = filterGroup[0] + '$';
          items = items.filter((i: string) => !i.startsWith(filterType));
        });
      } else {
        const filterGroup = key.split('$');
        const filterType = filterGroup[0] + '$';
        items = items.filter((i: string) => !i.startsWith(filterType));
      }
    }

    if (key instanceof Array) {
      for (let idx = 0; idx < key.length; idx++) {
        const k = key[idx];
        if (k) {
          if (!props.selectedFilter.includes(k)) {
            items.push(k);
          } else {
            items = items.filter((i: string) => i !== k);
          }
        }
      }
    } else {
      if (!key) return;
      if (!props.selectedFilter.includes(key)) {
        items.push(key);
      } else {
        items = items.filter((i: string) => i !== key);
      }
    }

    // Never drop a Standard View's locked defining filter.
    (props.lockedFilterValues ?? []).forEach((value) => {
      if (!items.includes(value)) items.push(value);
    });

    props.updateSelectedFilter(items);
  };

  //
  // Specific helpers
  //
  const getTemplateOptions = (templates: Task[]): IComboBoxOption[] => {
    const options: IComboBoxOption[] = [
      {
        key: 0,
        text: '',
      },
    ];

    if (templates.length === 0 && !isLoading) {
      options.push({
        key: -1,
        text: t('translation:General.Notifications.NoItemsFilter'),
        disabled: true,
      });
    } else {
      options.push(
        ...templates.map((t) => {
          return {
            key: t.taskId,
            text: t.name,
          };
        }),
      );
    }

    return options;
  };

  //
  // Main render
  //
  const filterCount = getTotalFilterCount(props.selectedFilter);

  return (
    <IPStack horizontal verticalAlign="center">
      <IPCommandBarButton
        iconProps={filterICon}
        className="calloutTargetFilterButton"
        text={t('tasks:Filter.Button', { count: filterCount })}
        onClick={() => props.toggleDialog(!props.isOpen)}
        styles={globalSceneBarItemStyles}
      />
      {filterCount > 0 && !props.hideClear && (
        <IPIconButton styles={globalSceneBarItemStyles} onClick={() => clearFilter()} iconProps={cancelIcon} />
      )}
      <IPCallout
        alignTargetEdge={true}
        gapSpace={0}
        target=".calloutTargetFilterButton"
        isBeakVisible={false}
        directionalHint={IPDirectionalHint.bottomLeftEdge}
        hidden={!props.isOpen}
        onDismiss={() => props.toggleDialog(false)}
      >
        <IPStack styles={globalStackStylesHeight100PaddingSmall} tokens={globalStackTokensGapSmall}>
          <IPStack.Item>
            <IPStack horizontal horizontalAlign={'space-between'} styles={globalStackStylesHeight100PaddingSmall}>
              <IPText variant={'mediumPlus'}>{t('tasks:Filter.Header')}</IPText>
              <IPLink onClick={handleFilterClear} underline>
                {t('tasks:Filter.Clear')}
              </IPLink>
            </IPStack>
          </IPStack.Item>
          <IPStack.Item styles={{ root: { height: 600, width: 300, position: 'relative' } }}>
            <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
              <Accordion
                disabled={globalFilterCount(appContext, GlobalFilterGroupKeys.standard) > 0}
                title={t('tasks:Filter.ISONorms', {
                  count: getSelectedFilterCount(FilterTaskGroupKeys.standard),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  {appContext.globalDataCache.norms.items.map((norm: Norm) => (
                    <IPCheckbox
                      key={norm.normId}
                      label={norm.name}
                      checked={props.selectedFilter.includes(FilterTaskGroupKeys.standard + norm.normId.toString())}
                      onChange={() => handleSelectChange(FilterTaskGroupKeys.standard + norm.normId.toString())}
                    />
                  ))}
                </IPStack>
              </Accordion>
              {hasUserFeature(appContext, FeatureTypes.RBAC) && (
                <Accordion
                  title={t('adminAuth:AuthSchemaPicker.Filter.Shared', {
                    count: getSelectedFilterCount(FilterTaskGroupKeys.shared),
                  })}
                >
                  <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                    <IPCheckbox
                      key={'everyone'}
                      label={t('adminAuth:AuthSchemaPicker.Placeholder')}
                      checked={props.selectedFilter.includes(FilterTaskGroupKeys.shared + 'everyone')}
                      onChange={() => {
                        handleSelectChange(FilterTaskGroupKeys.shared + 'everyone');
                      }}
                    />
                    <IPSeparator styles={{ root: { height: 1 } }} />
                    {appContext.globalDataCache.authSchemas
                      .getItemsForMember(appContext.user.id)
                      .map((schema: AuthSchema) => (
                        <IPCheckbox
                          key={schema.authSchemaId}
                          label={schema.name}
                          checked={props.selectedFilter.includes(FilterTaskGroupKeys.shared + schema.authSchemaId)}
                          onChange={() => handleSelectChange(FilterTaskGroupKeys.shared + schema.authSchemaId)}
                        />
                      ))}
                  </IPStack>
                </Accordion>
              )}
              <Accordion
                title={t('tasks:Filter.Assignment', {
                  count: getSelectedFilterCount(FilterTaskGroupKeys.assignment),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <IPCheckbox
                    key={'notassigned'}
                    label={t('tasks:Filter.NotAssignment')}
                    checked={props.selectedFilter.includes(FilterTaskGroupKeys.assignment + 'notassigned')}
                    onChange={() => {
                      const user = getUserIdFromFilter(props.selectedFilter);
                      handleSelectChange([FilterTaskGroupKeys.assignment + 'notassigned', user || '']);
                    }}
                  />
                  <IPSeparator styles={{ root: { height: 1 } }} />
                  <UserPicker
                    users={[]}
                    selectedItemId={getUserIdFromFilter(props.selectedFilter)}
                    onSelect={(item: User | undefined, removedId?: string) => {
                      if (item) {
                        handleSelectChange(FilterTaskGroupKeys.assignment + item.id);
                      } else if (removedId) {
                        handleSelectChange(FilterTaskGroupKeys.assignment + removedId);
                      }
                    }}
                    disabled={props.selectedFilter.includes('notassigned')}
                    placeHolder={t('tasks:Filter.AssignmentPlaceholder')}
                  />
                </IPStack>
              </Accordion>
              <Accordion
                title={t('tasks:Filter.Status', { count: getSelectedFilterCount(FilterTaskGroupKeys.status) })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <IPCheckbox
                    key={'open'}
                    label={t('tasks:Filter.StatusOpen')}
                    checked={props.selectedFilter.includes(FilterTaskGroupKeys.status + 'open')}
                    onChange={() => handleSelectChange(FilterTaskGroupKeys.status + 'open')}
                  />
                  <IPSeparator styles={{ root: { height: 1 } }} />
                  {appContext.globalDataCache.taskStates.items.map((taskState: TaskState) => (
                    <IPCheckbox
                      key={taskState.taskStateId}
                      label={taskState.state}
                      checked={props.selectedFilter.includes(
                        FilterTaskGroupKeys.status + taskState.taskStateId.toString(),
                      )}
                      onChange={() => handleSelectChange(FilterTaskGroupKeys.status + taskState.taskStateId.toString())}
                    />
                  ))}
                </IPStack>
              </Accordion>
              <Accordion
                title={t('tasks:Filter.StartDate', { count: getSelectedFilterCount(FilterTaskGroupKeys.startDate) })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  {startDateList.map((startDate: ICheckbox) => (
                    <IPCheckbox
                      key={startDate.key}
                      label={startDate.label}
                      checked={props.selectedFilter.includes(FilterTaskGroupKeys.startDate + startDate.key)}
                      onChange={() => handleSelectChange(FilterTaskGroupKeys.startDate + startDate.key)}
                    />
                  ))}
                </IPStack>
              </Accordion>
              <Accordion
                title={t('tasks:Filter.Deadline', { count: getSelectedFilterCount(FilterTaskGroupKeys.deadline) })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  {deadlineList.map((deadline: ICheckbox) => (
                    <IPCheckbox
                      key={deadline.key}
                      label={deadline.label}
                      checked={props.selectedFilter.includes(FilterTaskGroupKeys.deadline + deadline.key)}
                      onChange={() => handleSelectChange(FilterTaskGroupKeys.deadline + deadline.key)}
                    />
                  ))}
                </IPStack>
              </Accordion>
              <Accordion
                title={t('tasks:Filter.FollowUpTitle', {
                  count: getSelectedFilterCount(FilterTaskGroupKeys.followUp),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  {followUpFilterList.map((item: ICheckbox) => (
                    <IPCheckbox
                      key={item.key}
                      label={item.label}
                      checked={props.selectedFilter.includes(FilterTaskGroupKeys.followUp + item.value)}
                      onChange={() => handleSelectChange(FilterTaskGroupKeys.followUp + item.value)}
                    />
                  ))}
                </IPStack>
              </Accordion>
              <Accordion title={t('tasks:Filter.Tags', { count: getSelectedFilterCount(FilterTaskGroupKeys.tag) })}>
                <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
                  <KeyValueTagPicker
                    maxTagWidth={250}
                    selectedTags={getTagsFromFilter(props.selectedFilter)}
                    onAdd={(item: Tag) => handleSelectChange(FilterTaskGroupKeys.tag + item.tagId.toString())}
                    onRemove={(item: Tag) => handleSelectChange(FilterTaskGroupKeys.tag + item.tagId.toString())}
                  />
                  <IPCheckbox
                    label={t('tasks:Filter.TagGroupAnd')}
                    checked={taskFilterAnd}
                    onChange={(ev, checked) => {
                      setTaskFilterAnd(checked ?? false);
                      appContext.globalDataCache.userSettings.set(TaskTagFilterGroupAnd, checked ?? false);
                      //when user changes this, remove the value from the filter
                      const newFilter = [...props.selectedFilter];
                      const idx = newFilter.findIndex((n) => n.startsWith(FilterTaskGroupKeys.useAndForTagGroups));
                      if (idx >= 0) newFilter.splice(idx, 1);
                      //update the filter and save the user setting
                      props.updateSelectedFilter(newFilter);
                      appContext.globalDataCache.userSettings.save(TaskTagFilterGroupAnd, checked ?? false); //async so set first
                    }}
                  />
                </IPStack>
              </Accordion>
              <Accordion
                title={t('tasks:Filter.TagGroups', { count: getSelectedFilterCount(FilterTaskGroupKeys.tagGroup) })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
                  <SingleTagPicker
                    tags={appContext.globalDataCache.tags.getGroups()}
                    selectedTags={getTagGroupsFromFilter(props.selectedFilter)}
                    onAdd={(item: ISingleTag) =>
                      handleSelectChange(FilterTaskGroupKeys.tagGroup + item.tagName.toString())
                    }
                    onRemove={(item: ISingleTag) =>
                      handleSelectChange(FilterTaskGroupKeys.tagGroup + item.tagName.toString())
                    }
                  />
                </IPStack>
              </Accordion>
              <Accordion
                title={t('tasks:Filter.TagCategories', {
                  count: getSelectedFilterCount(FilterTaskGroupKeys.tagCategory),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
                  {Tag.getSystemTypeOptions(t)
                    .filter((a) => a.key !== TagSystemTypes.None)
                    .map((option) => {
                      const tagCategoryValue = FilterTaskGroupKeys.tagCategory + option.key.toString();
                      const isLocked = props.lockedFilterValues?.includes(tagCategoryValue) ?? false;

                      return (
                        <IPCheckbox
                          key={option.key}
                          label={option.text}
                          checked={props.selectedFilter.includes(tagCategoryValue)}
                          disabled={isLocked}
                          onChange={() => {
                            if (!isLocked) handleSelectChange(tagCategoryValue);
                          }}
                        />
                      );
                    })}
                </IPStack>
              </Accordion>
              <Accordion
                title={t('tasks:Filter.Context', { count: getSelectedFilterCount(FilterTaskGroupKeys.context) })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <EntityPicker
                    suggestionLimit={4}
                    entities={allEntities}
                    selectedEntity={getContextEntityFromFilter()}
                    addSelectedEntity={(item) => {
                      switch (item.typeOfEntity) {
                        case EntityTypes.Control:
                          updateFilterContext(FilterTaskGroupKeys.context + controlIdSearchPrepend + item.entityId);
                          break;
                        case EntityTypes.Requirement:
                          updateFilterContext(FilterTaskGroupKeys.context + themeIdSearchPrepend + item.entityId);
                          break;
                        default:
                          updateFilterContext(
                            `${FilterTaskGroupKeys.context}${item.typeOfEntity as number}_${item.entityId}`,
                          );
                          break;
                      }
                    }}
                    loadData={loadDataForPicker}
                    isLoading={isLoading}
                    showHeader={false}
                    setSearchTextToSelectedEntity={true}
                    clearSearchText={() => {
                      removeFilterContext();
                    }}
                    isOnPanel={false}
                  />
                </IPStack>
              </Accordion>
              {props.showWorkableTypeFilter && (
                <Accordion
                  title={t('tasks:Filter.WorkableTypes', {
                    count:
                      getSelectedFilterCount(FilterTaskGroupKeys.workableTaskType) +
                      getSelectedFilterCount(FilterTaskGroupKeys.taskKind),
                  })}
                >
                  <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                    {Object.entries(TaskWorkableTypes)
                      .filter(([_, value]) => typeof value === 'number' && value > 0)
                      .filter(([_, value]) => legacyOutlookSeriesEnabled || value !== TaskWorkableTypes.LegacyRecurring)
                      .map(([_, value]) => (
                        <IPCheckbox
                          key={value}
                          label={Task.getWorkableTypeText(value as TaskWorkableTypes, t)}
                          checked={props.selectedFilter.includes(FilterTaskGroupKeys.workableTaskType + value)}
                          onChange={() => handleSelectChange(FilterTaskGroupKeys.workableTaskType + value)}
                        />
                      ))}
                    {/* PLAN-075: the assessment tasks, by their kind (a single task or a recurring one alike) */}
                    {isAuditCriteriaPreviewEnabled(appContext) && (
                      <IPCheckbox
                        label={t('tasks:Filter.Assessments')}
                        checked={props.selectedFilter.includes(FilterTaskGroupKeys.taskKind + TaskKinds.Assessment)}
                        onChange={() => handleSelectChange(FilterTaskGroupKeys.taskKind + TaskKinds.Assessment)}
                      />
                    )}
                  </IPStack>
                </Accordion>
              )}
              {props.showTemplateFilter && (
                <Accordion
                  title={t('tasks:Filter.Template', {
                    count: getSelectedFilterCount(FilterTaskGroupKeys.template),
                  })}
                >
                  <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10, height: 200 } }}>
                    <IPComboBox
                      disabled={isLoading}
                      useComboBoxAsMenuWidth
                      allowFreeform={false}
                      autoComplete="on"
                      styles={globalComboContainerHeight}
                      selectedKey={getTemplateEntityFromFilter() ?? 0}
                      options={getTemplateOptions(appContext.globalDataCache.templates.items)}
                      onChange={(ev, option) => {
                        const templateId = option?.key as number;
                        if (templateId > 0) {
                          handleSelectChange(FilterTaskGroupKeys.template + templateId, true);
                        } else {
                          clearFilterForType(FilterTaskGroupKeys.template);
                        }
                      }}
                    />
                  </IPStack>
                </Accordion>
              )}
            </IPScrollablePane>
          </IPStack.Item>
        </IPStack>
      </IPCallout>
    </IPStack>
  );
};

export default FilterTasks;
